import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { API } from "@/utils/api";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Check, Share2, MessageCircle, ArrowLeft, FileText,
} from "lucide-react";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function toWaPhone(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// GST UQC units used on invoices. Quote units map: kg -> KGS, box/boxes -> BOX, anything else -> NOS.
const UNITS = [["KGS", "KGS (kilograms)"], ["BOX", "BOX (boxes)"], ["NOS", "NOS (numbers)"]];
const toUnit = (u) => { const x = String(u || "").toLowerCase(); return x.startsWith("kg") ? "KGS" : x.startsWith("box") ? "BOX" : "NOS"; };

// One quote line -> one invoice line. Keeps qty x rate equal to the quote's taxable value (after any discount).
function lineFromQuote(l) {
  const qty = Number(l.qty) || 1;
  const taxable = Number(l.taxable_amount) || 0;
  const base = (l.service_name || "") + (l.description ? ` - ${l.description}` : "");
  const eff = r2(taxable / qty);
  if (Math.abs(r2(eff * qty) - r2(taxable)) < 0.005) return { description: base, hsn_code: l.sac_code || "", qty, unit: toUnit(l.uom), rate: eff };
  return { description: `${base} (${qty} ${l.uom && l.uom !== "lumpsum" ? l.uom : "units"}, after discount)`, hsn_code: l.sac_code || "", qty: 1, unit: "NOS", rate: r2(taxable) };
}

const emptyLine = () => ({ description: "", hsn_code: "", qty: 1, unit: "NOS", rate: 0 });
const emptyParty = () => ({ name: "", gstin: "", address: "", phone: "", email: "", pan: "" });

export default function ManualInvoiceNew() {
  const navigate = useNavigate();
  const { invoiceNumber: editInvoiceNumber } = useParams();
  const isEdit = Boolean(editInvoiceNumber);
  const location = useLocation();
  const fromQuote = !isEdit ? (location.state?.fromQuote || null) : null;   // set by "Convert to invoice" on a quotation
  const quoteRates = fromQuote ? [...new Set((fromQuote.lines || []).map((l) => Number(l.gst_rate)))] : [];
  const [quoteRate, setQuoteRate] = useState(null);

  // Customer picker
  const [custQ, setCustQ] = useState("");
  const [custOptions, setCustOptions] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [custId, setCustId] = useState(null);
  const [custLoading, setCustLoading] = useState(false);

  // Bill-to / Ship-to
  const [billTo, setBillTo] = useState(emptyParty());
  const [shipSame, setShipSame] = useState(true);
  const [shipTo, setShipTo] = useState(emptyParty());
  const [updateProfile, setUpdateProfile] = useState(true);

  // Line items
  const [lineItems, setLineItems] = useState([emptyLine()]);
  const [gstRate, setGstRate] = useState(18);

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // created/updated invoice, once successful
  const [shareBusy, setShareBusy] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(isEdit);
  const [loadError, setLoadError] = useState(false);

  const custBoxRef = useRef(null);

  // Prefill when editing an existing invoice
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await api.get(`/v2/invoices/${editInvoiceNumber}`);
        const inv = r.data || {};
        if (inv.invoice_type !== "manual") {
          toast.error("Only manual invoices can be edited");
          setLoadError(true);
          return;
        }
        if (inv.payment_status !== "UNPAID") {
          toast.error("Only unpaid invoices can be edited");
          setLoadError(true);
          return;
        }
        const bt = inv.bill_to || {};
        const st = inv.ship_to || {};
        setCustId(inv.customer_id);
        setCustQ(bt.name || inv.customer_name || "Customer");
        setBillTo({
          name: bt.name || "",
          gstin: bt.gstin || "",
          address: bt.address || "",
          phone: bt.phone || "",
          email: bt.email || "",
          pan: bt.pan || "",
        });
        const sameAsBill =
          !st.name ||
          (st.name === bt.name && (st.gstin || "") === (bt.gstin || "") &&
            (st.address || "") === (bt.address || "") && (st.phone || "") === (bt.phone || ""));
        setShipSame(sameAsBill);
        setShipTo({
          name: st.name || "",
          gstin: st.gstin || "",
          address: st.address || "",
          phone: st.phone || "",
          email: "",
          pan: "",
        });
        setLineItems(
          (inv.line_items && inv.line_items.length
            ? inv.line_items.map((li) => ({
                description: li.description || "",
                hsn_code: li.hsn_code || "",
                qty: li.qty ?? 1,
                unit: li.unit || "NOS",
                rate: li.rate ?? 0,
              }))
            : [emptyLine()])
        );
        const rate =
          inv.tax_type === "CGST_SGST"
            ? Math.round(((inv.cgst_pct || 0) + (inv.sgst_pct || 0)) * 100) / 100
            : inv.igst_pct || 18;
        setGstRate(rate === 5 ? 5 : 18);
      } catch (e) {
        toast.error("Couldn't load invoice for editing");
        setLoadError(true);
      } finally {
        setLoadingInvoice(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editInvoiceNumber]);

  // Prefill from an accepted quotation (everything stays editable)
  const applyQuote = (rate) => {
    setQuoteRate(rate);
    setLineItems((fromQuote.lines || []).filter((l) => Number(l.gst_rate) === rate).map(lineFromQuote));
    setGstRate(rate);
  };
  useEffect(() => {
    if (!fromQuote) return;
    if (quoteRates.length === 1) applyQuote(quoteRates[0]);
    const cs = fromQuote.customer_snapshot || {};
    if (cs.customer_id) pickCustomer({ customer_id: cs.customer_id, company_name: cs.name, gst_number: cs.gstin, mobile: cs.phone, email: cs.email });
    else { setCustQ(cs.name || ""); setCustOpen(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Customer typeahead
  useEffect(() => {
    if (!custOpen || isEdit) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/v2/customer-profiles/search?q=${encodeURIComponent(custQ)}`);
        setCustOptions(r.data?.results || []);
      } catch {
        setCustOptions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [custQ, custOpen, isEdit]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (custBoxRef.current && !custBoxRef.current.contains(e.target)) setCustOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pickCustomer = async (c) => {
    setCustId(c.customer_id);
    setCustQ(c.company_name || c.contact_person || "Customer");
    setCustOpen(false);
    setCustLoading(true);
    try {
      const r = await api.get(`/v2/customers/profile/${c.customer_id}`);
      const p = r.data || {};
      const user = p.user || {};
      setBillTo({
        name: p.company_name || p.contact_person || c.company_name || "Customer",
        gstin: p.gst_number || c.gst_number || "",
        address: p.address_line1 || "",
        phone: user.phone || p.mobile || c.mobile || "",
        email: user.email || p.email || c.email || "",
        pan: p.pan_number || c.pan_number || "",
      });
    } catch {
      // fall back to just the search-result fields
      setBillTo({
        name: c.company_name || c.contact_person || "Customer",
        gstin: c.gst_number || "",
        address: "",
        phone: c.mobile || "",
        email: c.email || "",
        pan: c.pan_number || "",
      });
      toast.error("Couldn't load full profile — fill in missing details manually");
    } finally {
      setCustLoading(false);
    }
  };

  const changeCustomer = () => {
    if (isEdit) return;
    setCustId(null);
    setCustQ("");
    setBillTo(emptyParty());
    setShipTo(emptyParty());
  };

  const updateLine = (idx, field, value) => {
    setLineItems((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addLine = () => setLineItems((rows) => [...rows, emptyLine()]);
  const removeLine = (idx) => setLineItems((rows) => (rows.length > 1 ? rows.filter((_, i) => i !== idx) : rows));

  const taxable = lineItems.reduce((sum, r) => sum + (Number(r.qty) || 0) * (Number(r.rate) || 0), 0);
  const gstAmount = Math.round(taxable * gstRate) / 100;
  const total = Math.round((taxable + gstAmount) * 100) / 100;

  const canSubmit =
    custId &&
    !(fromQuote && (fromQuote.reverse_charge || (quoteRates.length > 1 && quoteRate === null))) &&
    !loadingInvoice &&
    billTo.name.trim() &&
    lineItems.some((r) => r.description.trim() && Number(r.qty) > 0 && Number(r.rate) > 0) &&
    !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const items = lineItems
        .filter((r) => r.description.trim() && Number(r.qty) > 0 && Number(r.rate) > 0)
        .map((r) => ({
          description: r.description.trim(),
          hsn_code: r.hsn_code.trim() || null,
          qty: Number(r.qty),
          unit: r.unit || "NOS",
          rate: Number(r.rate),
        }));
      const payload = {
        customer_id: custId,
        gst_rate: Number(gstRate),
        line_items: items,
        bill_to: billTo,
        ship_to: shipSame ? null : shipTo,
        update_profile: updateProfile,
      };
      const r = isEdit
        ? await api.put(`/v2/invoices/manual/${editInvoiceNumber}`, payload)
        : await api.post(`/v2/invoices/manual`, payload);
      setResult(r.data);
      if (fromQuote) {   // remember the link on the quotation; the invoice is already created either way
        try { await api.post(`/v2/quotations/${fromQuote.id}/invoiced`, { invoice_number: r.data.invoice_number }); } catch { /* non-blocking */ }
      }
      toast.success(isEdit ? `Invoice ${r.data.invoice_number} updated` : `Invoice ${r.data.invoice_number} created`);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEdit ? "Failed to update invoice" : "Failed to create invoice"));
    } finally {
      setBusy(false);
    }
  };

  const publicPdfUrl = result ? `${API}/v2/invoices/public/${result.invoice_number}/${result.share_token}/pdf` : "";

  const shareWhatsApp = () => {
    if (!result) return;
    const phone = toWaPhone(billTo.phone);
    const msg = `Hi ${billTo.name}, here is your Sendtoo invoice ${result.invoice_number} for ₹${result.total_amount}. Download: ${publicPdfUrl}`;
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareNative = async () => {
    if (!result) return;
    setShareBusy(true);
    try {
      const resp = await fetch(publicPdfUrl);
      const blob = await resp.blob();
      const file = new File([blob], `Sendtoo_${result.invoice_number}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Invoice ${result.invoice_number}`,
          text: `Sendtoo invoice ${result.invoice_number} — ₹${result.total_amount}`,
        });
      } else if (navigator.share) {
        await navigator.share({
          title: `Invoice ${result.invoice_number}`,
          text: `Sendtoo invoice ${result.invoice_number} — ₹${result.total_amount}`,
          url: publicPdfUrl,
        });
      } else {
        await navigator.clipboard.writeText(publicPdfUrl);
        toast.success("Share not supported on this device — link copied instead");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't share — try the WhatsApp button instead");
    } finally {
      setShareBusy(false);
    }
  };

  const createAnother = () => {
    setResult(null);
    changeCustomer();
    setLineItems([emptyLine()]);
    setGstRate(18);
    setShipSame(true);
  };

  if (isEdit && loadingInvoice) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-16 px-4 text-center text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading invoice...
        </div>
      </DashboardLayout>
    );
  }

  if (isEdit && loadError) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-16 px-4 text-center">
          <p className="text-slate-600 mb-4">This invoice can't be edited.</p>
          <Button variant="outline" onClick={() => navigate("/admin/invoices")}>Back to Invoices</Button>
        </div>
      </DashboardLayout>
    );
  }

  if (result) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-10 px-4">
          <div className="bg-white border border-emerald-200 rounded-xl p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-emerald-700" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-1">
              {isEdit ? "Invoice Updated" : "Invoice Created"}
            </h2>
            <p className="text-slate-500 mb-6">
              {result.invoice_number} · LR {result.lr_number} · ₹{result.total_amount}
            </p>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <Button
                onClick={shareWhatsApp}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="manual-invoice-share-whatsapp"
              >
                <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
              </Button>
              <Button
                onClick={shareNative}
                disabled={shareBusy}
                variant="outline"
                data-testid="manual-invoice-share-native"
              >
                {shareBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Share2 className="w-4 h-4 mr-1" />}
                Share...
              </Button>
            </div>

            <div className="flex items-center justify-center gap-2 mb-6">
              <a
                href={publicPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-slate-600 hover:text-slate-800 underline inline-flex items-center gap-1"
              >
                <FileText className="w-4 h-4" /> View / Download PDF
              </a>
            </div>

            <div className="flex justify-center gap-3">
              {!isEdit && <Button variant="outline" onClick={createAnother}>Create Another</Button>}
              {isEdit && <Button variant="outline" onClick={() => navigate("/admin/invoices")}>Back to Invoices</Button>}
              <Button
                onClick={() => navigate(`/admin/invoices/${result.invoice_number}`)}
                className="bg-slate-800 hover:bg-slate-900 text-white"
              >
                View Invoice
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto py-8 px-4">
        <button
          onClick={() => navigate("/admin/invoices")}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Invoices
        </button>

        <h1 className="text-2xl font-bold text-slate-800 mb-1">
          {isEdit ? `Edit Invoice ${editInvoiceNumber}` : "Create Manual Invoice"}
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          {isEdit
            ? "Only unpaid manual invoices can be edited. The invoice number, LR number and share link stay the same."
            : "For billing not tied to a shipment (e.g. transportation services). A Sendtoo LR number and invoice number are generated automatically."}
        </p>

        {fromQuote && (
          <div className="mb-5 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900" data-testid="from-quote-banner">
            <p className="font-semibold">Invoice from quotation {fromQuote.quote_number}</p>
            <p className="text-xs mt-0.5">Customer, services, SAC codes and amounts are filled from the quote. Review and edit anything below before creating the invoice.</p>
            {fromQuote.reverse_charge && (
              <p className="mt-2 text-xs font-semibold text-red-700">This quote is marked "reverse charge". This invoice screen adds GST to the bill, so it cannot be used for a reverse-charge invoice. Do not create it here.</p>
            )}
            {quoteRates.length > 1 && (
              <div className="mt-2 text-xs">
                <p className="mb-1">The quote has services at different GST rates, and one invoice carries one rate. Choose which to invoice now (convert again for the other):</p>
                <div className="flex gap-2">
                  {quoteRates.map((rt) => (
                    <button key={rt} type="button" onClick={() => applyQuote(rt)}
                      className={`px-3 py-1 rounded-md border text-xs font-semibold ${quoteRate === rt ? "bg-[#159DFF] text-white border-[#159DFF]" : "bg-white text-slate-700 border-slate-300"}`}>{rt}% services</button>
                  ))}
                </div>
              </div>
            )}
            {!fromQuote.customer_snapshot?.customer_id && <p className="mt-2 text-xs text-amber-800">This quote is for a prospect with no customer account. Pick or create the customer below.</p>}
          </div>
        )}

        {/* Customer */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Customer</h2>
          {custId ? (
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <span className="text-sm text-slate-800">{billTo.name}</span>
              {!isEdit && (
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                  onClick={changeCustomer}
                >
                  Change
                </button>
              )}
            </div>
          ) : (
            <div className="relative" ref={custBoxRef}>
              <Input
                autoFocus
                value={custQ}
                onChange={(e) => { setCustQ(e.target.value); setCustOpen(true); }}
                onFocus={() => setCustOpen(true)}
                placeholder="Search customer by name, phone or GSTIN"
                data-testid="manual-invoice-customer-search"
              />
              {custOpen && custQ.trim().length >= 2 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
                  {custOptions.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-slate-400">No matches</div>
                  ) : (
                    custOptions.map((c) => (
                      <button
                        key={c.customer_id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                        onClick={() => pickCustomer(c)}
                      >
                        {c.company_name || c.contact_person || "Customer"}
                        {c.gst_number ? ` — ${c.gst_number}` : ""}
                      </button>
                    ))
                  )}
                  <a
                    href="/admin/customer-mgmt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 border-t border-slate-100"
                  >
                    + New Customer (opens in new tab)
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bill To / Ship To */}
        {custId && (
          <div className="grid md:grid-cols-2 gap-5 mb-5">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Bill To</h2>
              {custLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400 py-4">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading profile...
                </div>
              ) : (
                <div className="space-y-3">
                  <Field label="Name">
                    <Input value={billTo.name} onChange={(e) => setBillTo({ ...billTo, name: e.target.value })} />
                  </Field>
                  <Field label="GSTIN">
                    <Input value={billTo.gstin} onChange={(e) => setBillTo({ ...billTo, gstin: e.target.value.toUpperCase() })} placeholder="e.g. 37AAACX1234F1Z5" />
                  </Field>
                  <Field label="Address">
                    <Input value={billTo.address} onChange={(e) => setBillTo({ ...billTo, address: e.target.value })} />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Phone">
                      <Input value={billTo.phone} onChange={(e) => setBillTo({ ...billTo, phone: e.target.value })} />
                    </Field>
                    <Field label="PAN">
                      <Input value={billTo.pan} onChange={(e) => setBillTo({ ...billTo, pan: e.target.value.toUpperCase() })} />
                    </Field>
                  </div>
                  <Field label="Email">
                    <Input value={billTo.email} onChange={(e) => setBillTo({ ...billTo, email: e.target.value })} />
                  </Field>
                  <label className="flex items-center gap-2 text-xs text-slate-500 pt-1">
                    <input
                      type="checkbox"
                      checked={updateProfile}
                      onChange={(e) => setUpdateProfile(e.target.checked)}
                    />
                    Save GSTIN/address changes to this customer's profile
                  </label>
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Ship To</h2>
                <label className="flex items-center gap-1.5 text-xs text-slate-500">
                  <input
                    type="checkbox"
                    checked={shipSame}
                    onChange={(e) => setShipSame(e.target.checked)}
                  />
                  Same as Bill To
                </label>
              </div>
              {shipSame ? (
                <div className="text-sm text-slate-400 py-8 text-center">Same as Bill To party</div>
              ) : (
                <div className="space-y-3">
                  <Field label="Name">
                    <Input value={shipTo.name} onChange={(e) => setShipTo({ ...shipTo, name: e.target.value })} />
                  </Field>
                  <Field label="GSTIN">
                    <Input value={shipTo.gstin} onChange={(e) => setShipTo({ ...shipTo, gstin: e.target.value.toUpperCase() })} />
                  </Field>
                  <Field label="Address">
                    <Input value={shipTo.address} onChange={(e) => setShipTo({ ...shipTo, address: e.target.value })} />
                  </Field>
                  <Field label="Phone">
                    <Input value={shipTo.phone} onChange={(e) => setShipTo({ ...shipTo, phone: e.target.value })} />
                  </Field>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Line items */}
        {custId && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Line Items</h2>
            <div className="hidden md:grid grid-cols-[1fr_100px_80px_100px_110px_110px_36px] gap-2 text-xs font-semibold text-slate-400 uppercase px-1 mb-1">
              <div>Description</div>
              <div>HSN/SAC</div>
              <div>Qty</div>
              <div>Unit</div>
              <div>Rate (₹)</div>
              <div>Amount (₹)</div>
              <div></div>
            </div>
            <div className="space-y-2">
              {lineItems.map((row, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_100px_80px_100px_110px_110px_36px] gap-2 items-center">
                  <Input
                    value={row.description}
                    onChange={(e) => updateLine(idx, "description", e.target.value)}
                    placeholder="e.g. Hyderabad to Khammam Transportation"
                    data-testid={`manual-invoice-line-desc-${idx}`}
                  />
                  <Input
                    value={row.hsn_code}
                    onChange={(e) => updateLine(idx, "hsn_code", e.target.value)}
                    placeholder="HSN"
                  />
                  <Input
                    type="number" min="0" step="1"
                    value={row.qty}
                    onChange={(e) => updateLine(idx, "qty", e.target.value)}
                  />
                  <select value={row.unit || "NOS"} onChange={(e) => updateLine(idx, "unit", e.target.value)} title="Unit (GST UQC)"
                    data-testid={`manual-invoice-line-unit-${idx}`}
                    className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800">
                    {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <Input
                    type="number" min="0" step="0.01"
                    value={row.rate}
                    onChange={(e) => updateLine(idx, "rate", e.target.value)}
                />
                  <div className="text-sm text-slate-700 font-medium px-2">
                    ₹{((Number(row.qty) || 0) * (Number(row.rate) || 0)).toFixed(2)}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLine(idx)}
                    className="text-slate-300 hover:text-red-500 justify-self-center"
                    disabled={lineItems.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addLine} className="mt-3">
              <Plus className="w-4 h-4 mr-1" /> Add Line
            </Button>
          </div>
        )}

        {/* GST + totals */}
        {custId && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">GST Rate</h2>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setGstRate(18)}
                    className={`px-4 py-2 rounded-md border text-sm font-medium ${
                      gstRate === 18
                        ? "bg-slate-800 text-white border-slate-800"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid="manual-invoice-gst-18"
                >
                    18% — Logistics
                  </button>
                  <button
                    type="button"
                    onClick={() => setGstRate(5)}
                    className={`px-4 py-2 rounded-md border text-sm font-medium ${
                      gstRate === 5
                        ? "bg-emerald-700 text-white border-emerald-700"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid="manual-invoice-gst-5"
                  >
                    5% — Transportation
                  </button>
                </div>
              </div>
              <div className="text-right text-sm text-slate-600 space-y-1 min-w-[200px]">
                <div className="flex justify-between"><span>Taxable Amount</span><span>₹{taxable.toFixed(2)}</span></div>
                <div className="flex justify-between text-slate-400"><span>GST @ {gstRate}%</span><span>₹{gstAmount.toFixed(2)}</span></div>
                <div className="flex justify-between text-base font-bold text-slate-800 pt-1 border-t border-slate-100">
                  <span>Total</span><span>₹{total.toFixed(2)}</span>
                </div>
                <p className="text-xs text-slate-400 pt-1">
                  CGST+SGST vs IGST split is determined automatically from the customer's state.
                </p>
              </div>
            </div>
          </div>
        )}

        {custId && (
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate("/admin/invoices")}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={!canSubmit}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="manual-invoice-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              {isEdit ? "Update Invoice" : "Create Invoice"}
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
